'use client'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import type {
  NavigationLink,
  PromotionEvents,
} from '@susu/headless-commerce/gql/generated/graphql'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import { generateUrlFromLinkContent } from '@susu/headless-commerce/utils/UrlGenerator'
import {
  trackClickPromotion,
  trackImpressionPromotion,
} from '@susu/headless-commerce/utils/tracking/tracking'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef } from 'react'

import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import style from './TextAndImageColumnBannerV2.module.scss'

export type ClientTextAndImageColumnBannerV2Props = {
  children?: ReactNode
  link?: NavigationLink
}

export default function ClientTextAndImageColumnBannerV2({
  children,
  link,
}: Readonly<ClientTextAndImageColumnBannerV2Props>) {
  const locale = useLocale()
  const country = useCountry()
  const linkRef = useRef<HTMLAnchorElement>(null)
  const linkHref = generateUrlFromLinkContent(
    link as NavigationLink,
    country.siteID,
    locale,
  )
  const pageType = usePageType()

  const handleLinkClick = useCallback(() => {
    if (link?.promotionEvents) {
      trackClickPromotion(
        linkRef,
        link?.promotionEvents as PromotionEvents,
        pageType,
        { locale, country },
      )
    }
  }, [pageType, country, link?.promotionEvents, locale])

  useEffect(() => {
    if (linkRef.current) {
      trackImpressionPromotion(
        linkRef,
        (link?.promotionEvents as PromotionEvents) ?? {},
        pageType,
        {
          locale,
          country,
        },
      )
    }
  }, [pageType, country, link?.promotionEvents, locale])

  return (
    <a
      href={linkHref}
      ref={linkRef}
      onClick={handleLinkClick}
      className={style['textimage-column__link-wrapper']}
    >
      {children}
    </a>
  )
}
